<template>
    <div class="container">
        <v-title :lineage="lineage">
            {{pageTitle}}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages[addRoute.name].isAllowed"
                    :to="addRoute"
                    class="btn btn-link">Add Notification<v-icon class="margin-left-xs" icon="arrow-right"/></router-link>
            </div>
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-full">
                <div class="tile position-relative">
                    <v-inline-loader v-if="isLoading"/>
                    <notification-list
                        v-if="ruleSet && !hasError"
                        v-model="notifications"
                        :event="systemEvent"/>
                    <div
                        v-else-if="!ruleSet && hasError"
                        class="message message-negative stack-m">
                        <div class="message-content">
                            <p>There was an error while trying to access this event. Please try again later or contact support.</p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-full"
            v-if="$auth.$pages[rulesEngineRoute.name].isAllowed">
                <p class="text-center">Want to do more than send notifications?  <router-link :to="rulesEngineRoute">View all the rules and binds for this event in the Rules Engine</router-link></p>
            </div>
        </div>
    </div>
</template>

 <script>
    import EventModel from '@/common/models/rules/event-model';
    import vTitle from '@/components/v-title';
    import {vIcon} from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import NotificationList from './components/notification-list';
    import {extractCustomFieldResources} from '@/modules/rules-engine/components/condition-builder/get-fields-from-pathways';
    import RuleSetModel from '@/common/models/rules/rule-set-model';

    export default {
        name: 'event-notifications',
        mixins: [LoadStateMixin],
        components: {
            vTitle, vIcon, NotificationList, vInlineLoader,
        },
        props: {
            eventType: String,
            eventId: String,
        },
        computed: {
            notifications: {
                get() {
                    return this.ruleSet.binds.filter(bind => bind.hasEmailActions);
                },
                set(newVal) {
                    this.updateNotifications(newVal);
                },
            },
            addRoute() {
                return {name: 'add-notification'};
            },
            systemEvent() {
                return new EventModel({eventType: this.eventType});
            },
            pageTitle() {
                return this.$filters.unKebabCase(this.eventType);
            },
            rulesEngineRoute() {
                return {
                    name: 'view-system-event',
                    params: {
                        eventType: this.eventType,
                        view: 'binds',
                    },
                };
            },
            lineage() {
                return [
                    {
                        Notifications: {
                            name: 'notifications',
                        },
                    },
                ];
            },
        },
        data() {
            return {
                ruleSet: null,
                isLoading: false,
                hasError: false,
            };
        },
        methods: {
            /**
             * Return the correct API namespace based
             * on the type of event we're viewing.
             * @param callback
             */
            getDelegate(callback) {
                return this.$api(({events}) => callback(events));
            },
            async fetchData() {
                try {
                    await this.cacheCustomFields();
                    const params = {eventType: this.eventType};
                    const {fields} = await this.getDelegate(type => type.getRules(params));
                    this.setRuleSet(fields);
                    await this.$store.dispatch('rulesEngine/parseLabels', fields.rules);
                } catch (err) {
                    this.hasError = true;
                }
            },
            async cacheCustomFields() {
                const fetches = extractCustomFieldResources(this.systemEvent.pathways)
                    .map(resource => this.$store.dispatch('rulesEngine/fetchCustomFields', resource));
                await Promise.all(fetches);
            },
            async updateNotifications({notifications, message}) {
                this.updateRuleSetNotifications(notifications);
                await this.pushUpdate(message);
            },
            updateRuleSetNotifications(newList) {
                const binds = [
                    ...newList,
                    ...this.ruleSet.binds.filter(bind => !bind.hasEmailActions),
                ];
                this.ruleSet = new RuleSetModel({...this.ruleSet, binds});
            },
            setRuleSet(ruleSet) {
                this.ruleSet = new RuleSetModel(ruleSet);
            },
            async pushUpdate(message) {
                this.isLoading = true;
                try {
                    const filters = {
                        limit: 1,
                        eventType: this.eventType,
                    };
                    const {items} = await this.getDelegate(type => type.getRulesHistory(filters));
                    const [latest] = items;
                    // check if the latest event is of the same version
                    if (latest.fields.version !== this.ruleSet.version) {
                        this.$toast.negative(
                            'This event has been modified since you viewed it. Your modification was discarded and the data was refreshed.',
                            {delay: 10000},
                        );
                        await this.fetchData();
                        return;
                    }
                    const params = {
                        data: this.ruleSet.getPayload(),
                        eventType: this.eventType,
                    };
                    const {fields} = await this.getDelegate(type => type.updateRules(params));
                    // update local version
                    this.ruleSet.version = fields.version;
                    if (message) {
                        this.$toast.positive(message);
                    }
                } catch (error) {
                    this.$toast.fromError(error, `Unable to modify ${this.$filters.unKebabCase(this.eventType)} at this time.`);
                } finally {
                    this.isLoading = false;
                }
            },
        },
    };
</script>
